import {
  DASHBOARD_STAT_ROW_HEIGHT_PX,
  KPI_GOAL_LINE_PERCENT,
  KPI_MAX_GOAL_RATIO,
  SCREEN_HOURS_GREEN_MAX,
  STAFFER_STAT_CHART_BOTTOM_PERCENT,
} from "@/lib/constants";
import { formatting } from "@/lib/formatting";
import {
  DashboardComparisonMetric,
  DashboardKpiOverview,
  DashboardKpiPeriod,
  GoalPeriod,
} from "@/types";

// The KPI cards' border-t-2 accent and their 1px sides, which count against
// the fixed card height.
const CARD_BORDER_TOP_PX = 2;
const CARD_BORDER_BOTTOM_PX = 1;

// A month is treated as four weeks when a monthly goal is shown weekly.
const WEEKS_PER_MONTH = 4;

// Revenue goals round up to a clean $1,000 so the denominator never undershoots
// the real target and matches the compact "$21K" it renders as.
const REVENUE_GOAL_ROUNDING = 1000;

// Below this the period has barely started and a run-rate projection would be
// wild, so pacing is clamped here.
const MIN_ELAPSED_FRACTION = 0.05;

// Cycle order for the chevron on the comparison and trend cards.
const METRIC_CYCLE = [
  DashboardComparisonMetric.TIME_TO_SCREEN,
  DashboardComparisonMetric.SUBMITS,
  DashboardComparisonMetric.REVENUE,
];

export const dashboardKpis = {
  /**
   * The card's top border expressed in chart-area percent — negative, since the
   * chart area starts below the header. A bar drawn to it runs full-bleed to
   * the top of the card, over the title.
   */
  cardTopPercent: (headerBlockPx: number): number => {
    const contentHeightPx =
      DASHBOARD_STAT_ROW_HEIGHT_PX - CARD_BORDER_TOP_PX - CARD_BORDER_BOTTOM_PX;
    const chartHeightPx = contentHeightPx - headerBlockPx;
    // Up over the header and the top border itself, so the bar meets the very
    // edge of the card rather than stopping a border's width short of it.
    return -((headerBlockPx + CARD_BORDER_TOP_PX) / chartHeightPx) * 100;
  },

  /**
   * Where a submits or revenue value sits, as a percentage down from the plot's
   * top. Zero sits on the baseline and goal lands on the goal line, so the
   * scale is fixed to the target rather than to the series — a flat week and a
   * wild one look different. Beating goal climbs past the line and off the card.
   */
  goalAnchoredPercent: (value: number, goal: number): number => {
    if (goal <= 0) return STAFFER_STAT_CHART_BOTTOM_PERCENT;
    const goalRatio = Math.min(value / goal, KPI_MAX_GOAL_RATIO);
    return (
      STAFFER_STAT_CHART_BOTTOM_PERCENT -
      goalRatio * (STAFFER_STAT_CHART_BOTTOM_PERCENT - KPI_GOAL_LINE_PERCENT)
    );
  },

  /** The half of the payload the week/month toggle is currently showing. */
  periodFor: (
    overview: DashboardKpiOverview | undefined,
    period: GoalPeriod,
  ): DashboardKpiPeriod | undefined => {
    if (!overview) return undefined;
    return period === GoalPeriod.WEEK ? overview.week : overview.month;
  },

  /**
   * The goal a KPI is measured against. Team goals are stored monthly, so the
   * week view divides by four; `perStaffer` further divides by head count for
   * the views that grade one staffer at a time. Time to screen is a flat
   * target — it doesn't scale with either.
   */
  goalFor: ({
    metric,
    overview,
    perStaffer,
    period,
  }: {
    metric: DashboardComparisonMetric;
    overview: DashboardKpiOverview | undefined;
    perStaffer: boolean;
    period: GoalPeriod;
  }): number => {
    if (metric === DashboardComparisonMetric.TIME_TO_SCREEN) {
      return SCREEN_HOURS_GREEN_MAX;
    }
    if (!overview) return 0;

    const periodDivisor = period === GoalPeriod.WEEK ? WEEKS_PER_MONTH : 1;
    const stafferDivisor = perStaffer ? overview.numStaffers || 1 : 1;
    if (metric === DashboardComparisonMetric.SUBMITS) {
      return Math.ceil(overview.goals.submits / periodDivisor / stafferDivisor);
    }

    const revenue = overview.goals.revenue / periodDivisor / stafferDivisor;
    return Math.ceil(revenue / REVENUE_GOAL_ROUNDING) * REVENUE_GOAL_ROUNDING;
  },

  /** The next KPI in the cycle, wrapping back to time to screen. */
  nextMetric: (metric: DashboardComparisonMetric): DashboardComparisonMetric =>
    METRIC_CYCLE[(METRIC_CYCLE.indexOf(metric) + 1) % METRIC_CYCLE.length],

  /** Full-precision value: "13h", "$18,500", "8". */
  formatValue: (metric: DashboardComparisonMetric, value: number): string => {
    if (metric === DashboardComparisonMetric.TIME_TO_SCREEN) {
      return `${value.toString()}h`;
    }
    if (metric === DashboardComparisonMetric.REVENUE) {
      return formatting.currency(value);
    }
    return value.toString();
  },

  /** Space-constrained value for chart labels: "13h", "$18.5K", "8". */
  formatCompactValue: (
    metric: DashboardComparisonMetric,
    value: number,
  ): string => {
    if (metric === DashboardComparisonMetric.TIME_TO_SCREEN) {
      return `${value.toString()}h`;
    }
    if (metric === DashboardComparisonMetric.REVENUE) {
      return formatting.compactUsd(value, { decimals: 1 });
    }
    return value.toLocaleString();
  },

  /** How far through the period we are, for run-rate projections. */
  elapsedFraction: (periodStart: string, periodEnd: string): number => {
    const start = new Date(periodStart).getTime();
    const end = new Date(periodEnd).getTime();
    if (end <= start) return 1;

    const elapsed = (Date.now() - start) / (end - start);
    return Math.min(1, Math.max(MIN_ELAPSED_FRACTION, elapsed));
  },
};
